import { useState, useRef, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Container,
  Card,
  CardContent,
  Button,
  Alert,
  Avatar,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  stepConnectorClasses,
  Divider,
  Chip,
  CircularProgress,
  TextField,
  InputAdornment,
  IconButton as MuiIconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import {
  Bolt as BoltIcon,
  CheckCircle as CheckCircleIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  MeetingRoom as MeetingRoomIcon,
  Person as PersonIcon,
  Assignment as AssignmentIcon,
  HowToReg as HowToRegIcon,
  LockOpen as LockOpenIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Key as KeyIcon,
} from '@mui/icons-material'
import {
  Profile,
  RoomType,
  GENDER_LABELS,
  ROOM_TYPE_LABELS,
  Room,
  BusinessProfile,
  DatingProfile,
  JobProfile,
} from '@/types/profile'
import { RoomSubFieldDef, SubFormValues } from '@/types/roomSubInfo'
import { getRoom, joinRoom } from '@/services/roomService'
import { toRoomType } from '@/utils/roomType'
import { useMyProfile } from '@/contexts/ProfileContext'
import { useAuth } from '@/contexts/AuthContext'
import { SUB_FIELD_TYPE_LABELS } from '@/components/SubInfoForm'
import { RoomSubInfoRegistrationForm } from '@/components/RoomSubInfoRegistrationForm'
import { ProfileTabForm, ProfileTabFormHandle } from '@/components/ProfileTabForm'
import { GoogleLoginButton } from '@/components/GoogleLoginButton'

// ── Stepper カスタムコネクター ──────────────────────────────
const QrStepConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: { top: 18 },
  [`&.${stepConnectorClasses.active} .${stepConnectorClasses.line}`]: {
    borderColor: theme.palette.primary.main,
  },
  [`&.${stepConnectorClasses.completed} .${stepConnectorClasses.line}`]: {
    borderColor: theme.palette.primary.main,
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: theme.palette.divider,
    borderTopWidth: 2,
  },
}))

// ── Step アイコン ─────────────────────────────────────────
const STEP_ICONS = [LockOpenIcon, PersonIcon, AssignmentIcon, HowToRegIcon]

const QrStepIcon = ({ active, completed, icon }: { active?: boolean; completed?: boolean; icon: React.ReactNode }) => {
  const IconComp = STEP_ICONS[(icon as number) - 1]
  return (
    <Box
      sx={{
        width: 36, height: 36, borderRadius: '50%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: completed ? 'primary.main' : active ? 'primary.main' : 'grey.200',
        color: completed || active ? 'white' : 'text.disabled',
        transition: 'all 0.3s',
      }}
    >
      {completed
        ? <CheckCircleIcon sx={{ fontSize: 20 }} />
        : <IconComp sx={{ fontSize: 20 }} />
      }
    </Box>
  )
}

// ── プロフィールサマリーカード ──────────────────────────────
const ProfileSummaryCard = ({ profile }: { profile: Profile }) => {
  const getSubInfo = () => {
    if (profile.type === 'business') return `${profile.companyName} / ${profile.position}`
    if (profile.type === 'dating') return `${profile.age}歳 / ${GENDER_LABELS[profile.gender]} / ${profile.occupation}`
    return `${profile.lastPosition} / ${profile.education}`
  }
  const typeColors: Record<RoomType, string> = {
    business: '#6366f1', dating: '#ec4899', job: '#059669',
  }
  return (
    <Box
      sx={{
        display: 'flex', alignItems: 'center', gap: 2,
        p: 2, bgcolor: 'grey.50', borderRadius: 2, mb: 2,
        border: '1px solid', borderColor: 'divider',
      }}
    >
      <Avatar
        src={profile.photoUrl}
        sx={{ width: 52, height: 52, bgcolor: typeColors[profile.type], fontSize: '1.25rem' }}
      >
        {profile.name.charAt(0)}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography fontWeight={700} noWrap>{profile.name}</Typography>
        <Typography variant="body2" color="text.secondary" noWrap>{getSubInfo()}</Typography>
        {profile.pr && (
          <Typography variant="caption" color="text.disabled" noWrap
            sx={{ display: 'block', mt: 0.25 }}>
            {profile.pr}
          </Typography>
        )}
      </Box>
      <Chip
        label={ROOM_TYPE_LABELS[profile.type]}
        size="small"
        sx={{ fontSize: '0.65rem', flexShrink: 0 }}
      />
    </Box>
  )
}

// ── サブ情報サマリー ───────────────────────────────────────
const SubInfoSummaryCard = ({ values, fieldDefs }: { values: SubFormValues; fieldDefs: RoomSubFieldDef[] }) => (
  <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
    {fieldDefs.map((def) => {
      const v = values[def.id]
      const display =
        def.type === 'checkbox' ? (v ? '✓ はい' : '✗ いいえ')
        : def.type === 'date' && v ? new Date(v as string).toLocaleDateString('ja-JP')
        : String(v || '（未入力）')
      return (
        <Box key={def.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1, '&:last-child': { mb: 0 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body2" color="text.secondary">{def.label}</Typography>
            <Chip label={SUB_FIELD_TYPE_LABELS[def.type]} size="small" variant="outlined"
              sx={{ fontSize: '0.6rem', height: 16 }} />
          </Box>
          <Typography variant="body2" fontWeight={500}>{display}</Typography>
        </Box>
      )
    })}
  </Box>
)

// ── ステップ定義 ───────────────────────────────────────────
const STEP_LABELS = ['① Google認証', '② メイン情報', '③ サブ情報', '④ 入室確定']

// ── メインページ ──────────────────────────────────────────
export const RoomQrEntryPage = () => {
  const { roomId } = useParams<{ roomId: string }>()
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const { myProfiles, saveProfile } = useMyProfile()
  const { user } = useAuth()

  const [activeStep, setActiveStep] = useState(0)
  const [subValues, setSubValues] = useState<SubFormValues | null>(null)
  // ステップを行き来しても入力中の値を失わないための下書き
  const [subDraftValues, setSubDraftValues] = useState<SubFormValues>({})
  const [authError, setAuthError] = useState<string | null>(null)
  const [profileSaveError, setProfileSaveError] = useState<string | null>(null)
  const [savingProfile, setSavingProfile] = useState(false)
  const [room, setRoom] = useState<Room | null>(null)
  const [roomLoading, setRoomLoading] = useState(true)
  const [roomError, setRoomError] = useState<string | null>(null)
  const [subFieldDefs, setSubFieldDefs] = useState<RoomSubFieldDef[]>([])

  // 入室確定ステップ用
  const [roomPassword, setRoomPassword] = useState('')
  const [roomPasswordError, setRoomPasswordError] = useState<string | null>(null)
  const [enterRoomError, setEnterRoomError] = useState<string | null>(null)
  const [enteringRoom, setEnteringRoom] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  const profileTabFormRef = useRef<ProfileTabFormHandle>(null)

  useEffect(() => {
    const fetchRoom = async () => {
      if (!roomId) return
      
      try {
        setRoomLoading(true)
        setRoomError(null)
        
        const roomData = await getRoom(roomId)
        
        const mappedRoom: Room = {
          id: roomData.roomId,
          name: roomData.roomName,
          type: toRoomType(roomData.categoryType),
          eventDate: roomData.eventDatetime ? new Date(roomData.eventDatetime) : undefined,
          expiresAt: roomData.expirationDate ? new Date(roomData.expirationDate) : undefined,
          maxParticipants: roomData.maxParticipants,
          createdAt: roomData.createdAt ? new Date(roomData.createdAt) : new Date(),
          participants: [],
        }
        
        setRoom(mappedRoom)
      } catch (err) {
        console.error('Failed to fetch room:', err)
        const code = (err as { code?: string }).code
        if (code === 'functions/not-found' || code === 'functions/invalid-argument') {
          // 主催者がルームを削除した場合や、QRのURLが壊れている場合
          setRoomError('このルームは見つかりません。削除された可能性があります。主催者にご確認ください。')
        } else if (code === 'functions/unavailable' || code === 'functions/deadline-exceeded') {
          setRoomError('通信に失敗しました。電波状況を確認して、再読み込みしてください。')
        } else {
          setRoomError('ルーム情報の取得に失敗しました。時間をおいて再度お試しください。')
        }
      } finally {
        setRoomLoading(false)
      }
    }

    fetchRoom()
  }, [roomId])

  if (roomLoading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (roomError || !room) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, px: 3 }}>
        <Alert severity="error" sx={{ maxWidth: 480, width: '100%', borderRadius: 2 }}>
          {roomError || 'ルームが見つかりません'}
        </Alert>
        <Button variant="outlined" onClick={() => window.location.reload()}>
          再読み込みする
        </Button>
      </Box>
    )
  }

  const isExpired = room.expiresAt != null && new Date() > room.expiresAt

  if (isExpired) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, px: 3 }}>
        <Alert severity="error" sx={{ maxWidth: 480, width: '100%', borderRadius: 2 }}>
          <Typography fontWeight={700} gutterBottom>このルームは有効期限切れです</Typography>
          <Typography variant="body2">
            有効期限: {room.expiresAt!.toLocaleString('ja-JP')}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            このルームには入室できません。
          </Typography>
        </Alert>
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/')}>
          ホームに戻る
        </Button>
      </Box>
    )
  }

  const myProfile = myProfiles[room.type]
  const isAuthenticated = !!user

  const handleProfileValidSubmit = async (profile: BusinessProfile | DatingProfile | JobProfile) => {
    // 保存の完了を待たずに次へ進むと、保存に失敗しても気づけず、
    // プロフィール未登録のまま入室して参加者一覧に表示されなくなる
    setProfileSaveError(null)
    setSavingProfile(true)
    try {
      await saveProfile(profile, undefined, 'register', 2) // QR経路はrole_id=2（ユーザー）
      setActiveStep(2)
    } catch (error) {
      console.error('Failed to save profile:', error)
      setProfileSaveError('プロフィールの保存に失敗しました。時間をおいて再度お試しください。')
    } finally {
      setSavingProfile(false)
    }
  }

  const handleSubInfoComplete = (values: SubFormValues, defs: RoomSubFieldDef[]) => {
    setSubFieldDefs(defs)
    setSubValues(values)
    setActiveStep(3)
  }

  const handleEnterRoom = async () => {
    if (!roomPassword.trim()) {
      setRoomPasswordError('ルームパスワードを入力してください')
      return
    }

    if (!roomId) {
      return
    }

    setRoomPasswordError(null)
    setEnterRoomError(null)

    try {
      setEnteringRoom(true)

      // 入室直前にルームの有効性を再確認する。
      // 手続きは複数ステップに分かれており、画面を開いてから入室するまでの間に
      // 主催者がルームを削除したり有効期限を過ぎたりしうるため、
      // 初回読み込み時の判定だけでは防げない。
      const latest = await getRoom(roomId)
      if (latest.expirationDate && new Date(latest.expirationDate) < new Date()) {
        setEnterRoomError('このルームは有効期限切れのため入室できません。')
        return
      }

      await joinRoom(roomId, roomPassword)
      navigate(`/room/${room.id}`)
    } catch (error: unknown) {
      console.error('Failed to join room:', error)
      const firebaseError = error as { code?: string; message?: string }
      const code = firebaseError.code
      if (firebaseError.message?.includes('パスワードが正しくありません')) {
        setRoomPasswordError('パスワードが正しくありません')
      } else if (code === 'functions/not-found' || code === 'functions/invalid-argument') {
        setEnterRoomError('このルームは削除されたため入室できません。主催者にご確認ください。')
      } else if (code === 'functions/failed-precondition') {
        setEnterRoomError('このルームは有効期限切れのため入室できません。')
      } else if (code === 'functions/resource-exhausted') {
        setEnterRoomError('このルームは参加人数の上限に達しています。')
      } else {
        setEnterRoomError('入室処理に失敗しました。時間をおいて再度お試しください。')
      }
    } finally {
      setEnteringRoom(false)
    }
  }

  // ── Step 0: Google認証 ────────────────────────────────────
  const renderStep0 = () => (
    <>
      {isAuthenticated ? (
        <>
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5, p: 1.5,
            bgcolor: 'success.50', borderRadius: 2, border: '1px solid', borderColor: 'success.200',
          }}>
            <CheckCircleIcon color="success" sx={{ fontSize: 28 }} />
            <Box>
              <Typography fontWeight={700} color="success.dark">認証済み</Typography>
              <Typography variant="caption" color="text.secondary">Googleアカウントで認証されています</Typography>
            </Box>
          </Box>
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 2, p: 2,
            bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'divider', mb: 2,
          }}>
            <Avatar
              src={user?.photoURL ?? undefined}
              sx={{ width: 48, height: 48, bgcolor: 'primary.main' }}
            >
              {user?.displayName?.charAt(0)}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography fontWeight={700} noWrap>{user?.displayName}</Typography>
              <Typography variant="caption" color="text.secondary" noWrap>{user?.email}</Typography>
            </Box>
          </Box>
          <Button variant="contained" fullWidth size="large" onClick={() => setActiveStep(1)}
            endIcon={<ArrowForwardIcon />}
            sx={{ borderRadius: 2, py: 1.4, fontWeight: 700 }}>
            次へ
          </Button>
        </>
      ) : (
        <>
          <Alert severity="info" sx={{ mb: 2.5, borderRadius: 2 }}>
            <Typography variant="body2" fontWeight={600}>Googleアカウントでログインしてください</Typography>
            <Typography variant="body2">
              入室手続きにはGoogleアカウントによる認証が必要です。
            </Typography>
          </Alert>
          {authError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{authError}</Alert>
          )}
          <GoogleLoginButton
            roleId={2}
            onSuccess={() => { setAuthError(null); setActiveStep(1) }}
            onError={(msg) => setAuthError(msg)}
            sx={{ borderRadius: 2, py: 1.4, fontWeight: 700 }}
          />
        </>
      )}
    </>
  )

  // ── Step 1: メイン情報 ────────────────────────────────────
  const renderStep1 = () => (
    <>
      {myProfile ? (
        <>
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, p: 1.5,
            bgcolor: 'success.50', borderRadius: 2, border: '1px solid', borderColor: 'success.200',
          }}>
            <CheckCircleIcon color="success" sx={{ fontSize: 28 }} />
            <Box>
              <Typography fontWeight={700} color="success.dark">プロフィール登録済み</Typography>
              <Typography variant="caption" color="text.secondary">このプロフィールで入室します</Typography>
            </Box>
          </Box>
          <ProfileSummaryCard profile={myProfile} />
          <Button variant="contained" fullWidth size="large" onClick={() => setActiveStep(2)}
            endIcon={<ArrowForwardIcon />}
            sx={{ mt: 2, borderRadius: 2, py: 1.4, fontWeight: 700 }}>
            次へ
          </Button>
        </>
      ) : (
        <>
          <Alert severity="warning" sx={{ mb: 2.5, borderRadius: 2 }}>
            <Typography variant="body2" fontWeight={600}>プロフィールが未登録です</Typography>
            <Typography variant="body2">
              プロフィールを入力してください。{ROOM_TYPE_LABELS[room.type]}タブがこのルームのタイプです。
            </Typography>
          </Alert>
          <ProfileTabForm
            ref={profileTabFormRef}
            mode="register"
            initialRoomType={room.type}
            onValidSubmit={handleProfileValidSubmit}
          />
          {profileSaveError && (
            <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>{profileSaveError}</Alert>
          )}
          <Button
            variant="contained"
            fullWidth
            size="large"
            endIcon={savingProfile ? <CircularProgress size={20} color="inherit" /> : <ArrowForwardIcon />}
            onClick={() => profileTabFormRef.current?.submit()}
            disabled={savingProfile}
            sx={{ mt: 2, borderRadius: 2, py: 1.4, fontWeight: 700 }}
          >
            {savingProfile ? '保存中...' : '保存して次へ'}
          </Button>
        </>
      )}
    </>
  )

  // ── Step 2: サブ情報 ──────────────────────────────────────
  const renderStep2 = () => (
    <>
      {subValues ? (
        <>
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, p: 1.5,
            bgcolor: 'success.50', borderRadius: 2, border: '1px solid', borderColor: 'success.200',
          }}>
            <CheckCircleIcon color="success" sx={{ fontSize: 28 }} />
            <Box>
              <Typography fontWeight={700} color="success.dark">サブ情報登録済み</Typography>
              <Typography variant="caption" color="text.secondary">登録済みの内容で進みます</Typography>
            </Box>
          </Box>
          <SubInfoSummaryCard values={subValues} fieldDefs={subFieldDefs} />
          <Button variant="contained" fullWidth size="large" onClick={() => setActiveStep(3)}
            endIcon={<ArrowForwardIcon />}
            sx={{ mt: 2, borderRadius: 2, py: 1.4, fontWeight: 700 }}>
            次へ
          </Button>
        </>
      ) : (
        <>
          <Alert severity="info" sx={{ mb: 2.5, borderRadius: 2 }}>
            <Typography variant="body2" fontWeight={600}>追加情報を入力してください</Typography>
            <Typography variant="body2">
              主催者が設定した項目です。任意の入力で構いません。
            </Typography>
          </Alert>
          <RoomSubInfoRegistrationForm
            roomId={roomId!}
            submitLabel="保存して次へ"
            fieldSize="small"
            onSaved={handleSubInfoComplete}
            draftValues={subDraftValues}
            onDraftChange={setSubDraftValues}
          />
        </>
      )}
    </>
  )

  // ── Step 3: 入室確定 ──────────────────────────────────────
  const renderStep3 = () => {
    const profile = myProfiles[room.type]
    return (
      <>
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, mb: 3, p: 1.5,
          bgcolor: 'primary.50', borderRadius: 2, border: '1px solid', borderColor: 'primary.200',
        }}>
          <CheckCircleIcon color="primary" sx={{ fontSize: 28 }} />
          <Box>
            <Typography fontWeight={700} color="primary.dark">登録内容を確認してください</Typography>
            <Typography variant="caption" color="text.secondary">確認後、入室ボタンを押してください</Typography>
          </Box>
        </Box>

        <Typography variant="subtitle2" fontWeight={700} color="text.secondary"
          sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <PersonIcon sx={{ fontSize: 16 }} /> メイン情報
        </Typography>
        {profile && <ProfileSummaryCard profile={profile} />}

        <Divider sx={{ my: 2.5 }} />

        <Typography variant="subtitle2" fontWeight={700} color="text.secondary"
          sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <AssignmentIcon sx={{ fontSize: 16 }} /> サブ情報
        </Typography>
        {subValues && <SubInfoSummaryCard values={subValues} fieldDefs={subFieldDefs} />}

        <Divider sx={{ my: 2.5 }} />

        {/* ルームパスワード */}
        <Typography variant="subtitle2" fontWeight={700} color="text.secondary"
          sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <KeyIcon sx={{ fontSize: 16 }} /> ルームパスワード
        </Typography>
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 1.5 }}>
          会場で開示されるルームパスワードを入力してください
        </Typography>
        <TextField
          fullWidth
          size="small"
          label="ルームパスワード"
          placeholder="会場で開示されたパスワードを入力"
          type={showPassword ? 'text' : 'password'}
          value={roomPassword}
          onChange={(e) => { setRoomPassword(e.target.value); setRoomPasswordError(null) }}
          error={!!roomPasswordError}
          helperText={roomPasswordError}
          sx={{ mb: 2.5 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <MuiIconButton
                  size="small"
                  onClick={() => setShowPassword((v) => !v)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                </MuiIconButton>
              </InputAdornment>
            ),
          }}
        />

        {enterRoomError && (
          <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>
            {enterRoomError}
          </Alert>
        )}

        <Button
          variant="contained" fullWidth size="large" onClick={handleEnterRoom}
          startIcon={enteringRoom ? <CircularProgress size={20} color="inherit" /> : <MeetingRoomIcon />}
          disabled={enteringRoom}
          sx={{
            py: 1.8, borderRadius: 2, fontWeight: 700, fontSize: '1rem',
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            '&:hover': { background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' },
          }}
        >
          {enteringRoom ? '入室処理中...' : 'ルームに入室する'}
        </Button>
      </>
    )
  }

  const stepContent = [renderStep0, renderStep1, renderStep2, renderStep3]

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <AppBar position="sticky" sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Toolbar sx={{ px: { xs: 1.5, sm: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexGrow: 1 }}>
            <Box
              sx={{
                width: { xs: 32, sm: 36 }, height: { xs: 32, sm: 36 }, borderRadius: 1.5,
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <BoltIcon sx={{ color: 'white', fontSize: { xs: 18, sm: 20 } }} />
            </Box>
            <Box>
              <Typography variant="body1" fontWeight={700} sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                入室手続き
              </Typography>
              <Typography variant="caption" color="text.secondary">{room.name}</Typography>
            </Box>
          </Box>
          <Chip
            label={ROOM_TYPE_LABELS[room.type]}
            size="small"
            sx={{ fontSize: '0.7rem' }}
          />
        </Toolbar>
      </AppBar>

      {/* Stepper */}
      <Box sx={{ bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider', py: 2.5 }}>
        <Container maxWidth="sm">
          <Stepper
            activeStep={activeStep}
            alternativeLabel
            connector={<QrStepConnector />}
          >
            {STEP_LABELS.map((label, index) => (
              <Step key={label} completed={index < activeStep}>
                <StepLabel
                  StepIconComponent={(props) => <QrStepIcon {...props} />}
                  sx={{
                    '& .MuiStepLabel-label': {
                      fontSize: { xs: '0.6rem', sm: '0.7rem' },
                      fontWeight: activeStep === index ? 700 : 400,
                      color: activeStep === index ? 'primary.main' : 'text.secondary',
                    },
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Container>
      </Box>

      {/* Step Content */}
      <Container maxWidth="sm" sx={{ py: { xs: 2, sm: 3 }, px: { xs: 2, sm: 3 } }}>
        <Card>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight={700} sx={{ mb: 2 }}>
              {activeStep === 0 && 'Google認証'}
              {activeStep === 1 && 'メイン情報の確認'}
              {activeStep === 2 && 'サブ情報の入力'}
              {activeStep === 3 && '登録確定'}
            </Typography>

            {stepContent[activeStep]()}
          </CardContent>
        </Card>

        {/* 戻るボタン（Step 1 以降） */}
        {activeStep > 0 && (
          <Button
            variant="text" color="inherit" startIcon={<ArrowBackIcon />}
            onClick={() => setActiveStep((s) => s - 1)}
            sx={{ mt: 2, color: 'text.secondary' }}
          >
            前のステップに戻る
          </Button>
        )}
      </Container>
    </Box>
  )
}
